/**
 * @module flow-list/components/FlowTableView
 *
 * The classic table rendering of the flows list: DataTable wiring, column
 * definitions, expandable step preview, bulk-select checkboxes and server
 * pagination/sorting. Selection state and mutations stay in FlowList; this
 * component is presentational + column assembly.
 */
import React from 'react';
import type { ColumnDef, RowSelectionState, StockFeatures } from '@tanstack/react-table';
import { DataTable, type StatusTimelineEntry } from '@chatsdet/ui';
import type { FailureDiagnosticCategory } from '@chatsdet/types/issues';
import { useEligibilityModel } from '../../../../hooks/useEligibilityModel';
import type { WorkflowListItemWithDetails } from '../../../../hooks/useWorkflows';
import { WorkflowStepsPreview } from '../../../components/workflows/WorkflowStepsPreview';
import type { OpenWorkflowIssueSummary } from '../types';
import {
  FavoriteStar,
  FlowActionsMenu,
  FlowAuthorCell,
  FlowLastRunCell,
  FlowLastRunDurationCell,
  FlowNameCell,
  FlowRecentRuns,
  FlowLifecycleIcons,
  FlowTagsCell,
  type FlowActionHandlers,
  type LatestRunInfo,
} from './cells';

export type FlowTableViewProps = {
  workflows: WorkflowListItemWithDetails[];
  pagination?: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
  workflowTimelineEntries: Record<string, StatusTimelineEntry[]>;
  diagnosedCategoryByWorkflow: Record<string, FailureDiagnosticCategory | null>;
  openIssuesByWorkflow: Record<string, OpenWorkflowIssueSummary[]>;
  latestRunByWorkflow: Record<string, LatestRunInfo | undefined>;
  membersByUserId: Map<string, { displayName: string | null; primaryEmail: string | null }>;
  buildingWorkflowIds: Set<string>;
  newWorkflowIds: Set<string>;
  isRunsFetching: boolean;
  actionHandlers: FlowActionHandlers;
  onToggleFavorite: (workflowId: string, next: boolean) => void;
  rowSelection: RowSelectionState;
  onRowSelectionChange: (
    updaterOrValue: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)
  ) => void;
  onRowClick: (
    workflow: WorkflowListItemWithDetails,
    modifiers?: { metaKey: boolean; ctrlKey: boolean }
  ) => void;
  /** Links each row to the flow detail page (plain click navigates, cmd/ctrl-click opens a new tab). */
  getRowHref: (workflow: WorkflowListItemWithDetails) => string | undefined;
  onPageChange: (page: number) => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSortChange: (sortBy?: string, sortOrder?: 'asc' | 'desc') => void;
  /** Forwarded to DataTable — lets the toolbar lockup remove the top chrome. */
  containerClassName?: string;
};

export function FlowTableView({
  workflows,
  pagination,
  workflowTimelineEntries,
  diagnosedCategoryByWorkflow,
  openIssuesByWorkflow,
  latestRunByWorkflow,
  membersByUserId,
  buildingWorkflowIds,
  newWorkflowIds,
  isRunsFetching,
  actionHandlers,
  onToggleFavorite,
  rowSelection,
  onRowSelectionChange,
  onRowClick,
  getRowHref,
  onPageChange,
  sortBy,
  sortOrder,
  onSortChange,
  containerClassName,
}: FlowTableViewProps) {
  const eligibilityModel = useEligibilityModel();
  // Columns — universal lockup matching the cofactor design (chev / name+ID / status / runs / avatar / last-run / actions)
  const columns = React.useMemo<ColumnDef<StockFeatures, WorkflowListItemWithDetails>[]>(
    () => [
      {
        id: 'favorite',
        header: '',
        meta: { flex: 0.2, maxWidth: 36 },
        cell: ({ row }) => (
          <FavoriteStar
            isFavorite={row.original.isFavorite === true}
            onToggle={() => onToggleFavorite(row.original.id, !row.original.isFavorite)}
          />
        ),
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        enableSorting: true,
        meta: { flex: 3 },
        cell: ({ row }) => {
          const isBuilding = buildingWorkflowIds.has(row.original.id);
          const isNew = newWorkflowIds.has(row.original.id) && !isBuilding;
          return <FlowNameCell workflow={row.original} isBuilding={isBuilding} isNew={isNew} />;
        },
      },
      {
        id: 'status',
        header: 'Status',
        meta: { flex: 1, maxWidth: 130 },
        cell: ({ row }) => (
          <FlowLifecycleIcons item={row.original} eligibilityModel={eligibilityModel} />
        ),
      },
      {
        id: 'tags',
        header: 'Tags',
        meta: { flex: 1.5 },
        cell: ({ row }) => <FlowTagsCell tags={row.original.tags} />,
      },
      {
        id: 'recentRuns',
        header: 'Recent runs',
        // minWidth fits the worst-case cell: 10-slot strip, health icon,
        // diagnosis icon, "N issues" chip and the pass-rate slot. Narrower and
        // the percentage is pushed under the Author column.
        meta: { flex: 1.2, minWidth: 264, maxWidth: 280 },
        cell: ({ row }) => (
          <FlowRecentRuns
            entries={workflowTimelineEntries[row.original.id] ?? []}
            health={row.original.health}
            isPageLoading={isRunsFetching}
            diagnosedCategory={diagnosedCategoryByWorkflow[row.original.id] ?? null}
            openIssues={openIssuesByWorkflow[row.original.id] ?? []}
          />
        ),
      },
      {
        id: 'author',
        header: 'Author',
        meta: { flex: 0.4, maxWidth: 60 },
        cell: ({ row }) => (
          <FlowAuthorCell
            member={row.original.createdBy ? membersByUserId.get(row.original.createdBy) : null}
          />
        ),
      },
      {
        id: 'lastRunDuration',
        header: 'Duration',
        meta: { flex: 0.6, maxWidth: 90 },
        cell: ({ row }) => (
          <FlowLastRunDurationCell
            durationMs={latestRunByWorkflow[row.original.id]?.durationMs ?? null}
          />
        ),
      },
      {
        id: 'lastRun',
        header: 'Last run',
        meta: { flex: 0.6, maxWidth: 90 },
        cell: ({ row }) => <FlowLastRunCell latest={latestRunByWorkflow[row.original.id]} />,
      },
      {
        id: 'actions',
        header: '',
        meta: { flex: 0.3, maxWidth: 50 },
        cell: ({ row }) => (
          <FlowActionsMenu
            workflow={row.original}
            handlers={actionHandlers}
            eligibilityModel={eligibilityModel}
          />
        ),
      },
    ],
    [
      workflowTimelineEntries,
      openIssuesByWorkflow,
      isRunsFetching,
      buildingWorkflowIds,
      newWorkflowIds,
      membersByUserId,
      latestRunByWorkflow,
      actionHandlers,
      onToggleFavorite,
      eligibilityModel,
    ]
  );

  return (
    <DataTable
      columns={columns}
      data={workflows}
      containerClassName={containerClassName}
      getRowId={(row) => row.id}
      enableRowSelection
      rowSelection={rowSelection}
      onRowSelectionChange={onRowSelectionChange}
      onRowClick={(row: WorkflowListItemWithDetails, modifiers) => onRowClick(row, modifiers)}
      getRowHref={getRowHref}
      expandableRows={{
        render: (row) => (
          <WorkflowStepsPreview
            workflow={row.original}
            onNavigateToWorkflow={() => onRowClick(row.original)}
          />
        ),
        expanderColumnPlacement: 'start',
      }}
      serverPagination={
        pagination
          ? {
              page: pagination.page,
              pageSize: pagination.pageSize,
              pageCount: pagination.totalPages,
              totalItems: pagination.totalItems,
              onPageChange,
            }
          : undefined
      }
      serverSorting={{
        sortBy,
        sortOrder,
        onSortChange,
      }}
    />
  );
}
